/**
 * Which wordless tutorials this device has already seen.
 *
 * Device-local rather than per-student, and that is the whole reason this is not on the server: it
 * is about whether the person holding the phone has been shown how a game works, which is not a
 * fact about a child's learning and does not belong in their record. It also must not be a reason to
 * write to that record.
 *
 * This file used to hold the device write grant too — a profile id and a token, kept together
 * because either alone was useless. Both are gone: a student signs in now, so the session is the
 * grant and the learner record comes from `/v1/auth/me` (ADR-011). What is left is a preference.
 */

const TUTORIALS_KEY = "kiddora.tutorialsSeen";
const SITTINGS_KEY = "kiddora.sittings";

/**
 * Games whose demonstration shipped LATER than the game did.
 *
 * Arithmetic and sudoku went out with `showTutorial` accepted and ignored, and the runner marked the
 * tutorial as seen anyway — so a device that opened either one has a flag saying it watched a
 * demonstration that did not exist. Adding the demonstration alone would never show it to those
 * devices.
 *
 * Cleared once, on load. The cost of being wrong is a child seeing a short demonstration twice; the
 * cost of not doing it is a five-year-old meeting sudoku with no instruction at all, which is not
 * discoverable — "tap a square, then tap a shape" is not something they work out.
 */
const TUTORIALS_ADDED_LATE = ["math-arith-bare", "logic-sudoku-bare"];
const REPAIR_KEY = "kiddora.tutorialsRepaired.1";

function repairLateTutorials(seen: string[]): string[] {
  try {
    if (localStorage.getItem(REPAIR_KEY)) return seen;
    localStorage.setItem(REPAIR_KEY, "1");
    const kept = seen.filter((id) => !TUTORIALS_ADDED_LATE.includes(id));
    if (kept.length !== seen.length) {
      localStorage.setItem(TUTORIALS_KEY, JSON.stringify(kept));
    }
    return kept;
  } catch {
    return seen;
  }
}

export function hasSeenTutorial(taskId: string): boolean {
  try {
    const raw = localStorage.getItem(TUTORIALS_KEY);
    const seen = raw ? (JSON.parse(raw) as string[]) : [];
    return repairLateTutorials(seen).includes(taskId);
  } catch {
    // Unreadable means "not seen": showing a short demonstration twice is a far smaller cost
    // than a child facing a game whose rule was never demonstrated at all.
    return false;
  }
}

export function markTutorialSeen(taskId: string): void {
  try {
    const raw = localStorage.getItem(TUTORIALS_KEY);
    const seen = raw ? (JSON.parse(raw) as string[]) : [];
    if (!seen.includes(taskId)) {
      localStorage.setItem(TUTORIALS_KEY, JSON.stringify([...seen, taskId]));
    }
  } catch {
    /* The demonstration simply plays again next time. */
  }
}

/**
 * Which alternate form to play next, advancing every time a game is opened.
 *
 * **This is what stops a child seeing the same trials twice.** Every stimulus in the bundle is a
 * pure function of `(formId, trialIndex, difficulty)` — deliberately, so a trial can be re-scored
 * from the event record years later — and the form used to rotate by the DAY. Both halves of that
 * are right on their own and together they meant a child who left a game and came back got the same
 * cards in the same order, all day. It was reported as the game not being random. It is not random;
 * it was being asked the same question.
 *
 * Advancing per sitting keeps every guarantee: the form is recorded in `task_start`, so the trials
 * are still reproducible from the record, and practice-effect control gets BETTER rather than worse
 * — the daily rotation existed so that a child sees more than one form, and this reaches all of them
 * sooner.
 *
 * **It repeats after as many sittings as the task has forms**, which is two for most games and six
 * for the two letter games. Going further than that means either declaring more alternate forms or
 * recording a per-sitting seed, and the second is a protocol change.
 *
 * Counted per task, so opening one game does not skip a form in another. Device-local, like the
 * tutorial flags and for the same reason: which trials this phone has drawn is not a fact about a
 * child's learning and does not belong in their record.
 */
export function nextForm(taskId: string, formIds: readonly string[]): string {
  const fallback = formIds[0] ?? "A";
  if (formIds.length === 0) return fallback;
  try {
    const raw = localStorage.getItem(SITTINGS_KEY);
    const counts = raw ? (JSON.parse(raw) as Record<string, number>) : {};
    const next = (counts[taskId] ?? 0) + 1;
    localStorage.setItem(SITTINGS_KEY, JSON.stringify({ ...counts, [taskId]: next }));
    return formIds[next % formIds.length] ?? fallback;
  } catch {
    // Storage refused. The child gets the first form every time, which is the behaviour this
    // replaced — worse, and still a playable game.
    return fallback;
  }
}
